import React, { useState, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { Card, Button, Input, Modal } from '@components/common/UIComponents';
import {
  addHabitEntry,
  getHabitEntriesForDate,
  getHabitHistory,
  deleteHabitEntry,
  getUserSettings,
  updateUserSettings
} from '@services/firestoreService';
import { Beer, Cigarette, DollarSign, Calendar, TrendingDown, Target } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';

const HABIT_TYPES = {
  ALCOHOL: 'alcohol',
  SMOKING: 'smoking'
};

const HabitsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedDate] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [history, setHistory] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [settings, setSettings] = useState({
    alcoholPrice: 500, // per drink
    cigarettePrice: 20, // per cigarette
    quitDate: null
  });

  const [formData, setFormData] = useState({
    quantity: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [user, selectedDate]);

  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      console.log('Loading habits data for user:', user.uid);
      
      const [todayEntries, habitHistory, userSettings] = await Promise.all([
        getHabitEntriesForDate(user.uid, selectedDate),
        getHabitHistory(user.uid, 30),
        getUserSettings(user.uid)
      ]);
      
      console.log('Loaded data:', { 
        todayEntries: todayEntries.length, 
        habitHistory: habitHistory.length,
        userSettings 
      });
      
      setEntries(todayEntries);
      setHistory(habitHistory);
      
      if (userSettings?.habitSettings) {
        console.log('Setting habit settings:', userSettings.habitSettings);
        setSettings(prev => ({ ...prev, ...userSettings.habitSettings }));
      }
    } catch (error) {
      console.error('Error loading habits data:', error);
      alert(`Error loading data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (type) => {
    setSelectedType(type);
    setShowAddModal(true);
  };

  const handleAddEntry = async () => {
    if (!formData.quantity || formData.quantity <= 0) {
      console.log('Invalid quantity:', formData.quantity);
      alert('Please enter a valid quantity');
      return;
    }

    try {
      console.log('Adding habit entry:', {
        type: selectedType,
        quantity: formData.quantity,
        settings
      });

      await addHabitEntry(user.uid, {
        type: selectedType,
        quantity: parseInt(formData.quantity),
        notes: formData.notes,
        price: selectedType === HABIT_TYPES.ALCOHOL ? settings.alcoholPrice : settings.cigarettePrice
      });

      console.log('Habit entry added successfully');
      setShowAddModal(false);
      setFormData({ quantity: '', notes: '' });
      loadData();
    } catch (error) {
      console.error('Error adding habit entry:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await deleteHabitEntry(user.uid, entryId);
      loadData();
    } catch (error) {
      console.error('Error deleting entry:', error);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      const userSettings = await getUserSettings(user.uid) || {};
      await updateUserSettings(user.uid, {
        ...userSettings,
        habitSettings: settings
      });
      alert('Settings updated successfully!');
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleSetQuitDate = async () => {
    try {
      const userSettings = await getUserSettings(user.uid) || {};
      const newQuitDate = new Date().toISOString();
      await updateUserSettings(user.uid, {
        ...userSettings,
        habitSettings: {
          ...settings,
          quitDate: newQuitDate
        }
      });
      setSettings(prev => ({ ...prev, quitDate: newQuitDate }));
      alert('Quit date set! Stay strong! 💪');
      loadData();
    } catch (error) {
      console.error('Error setting quit date:', error);
    }
  };

  // Calculate stats
  const todayAlcohol = entries
    .filter(e => e.type === HABIT_TYPES.ALCOHOL)
    .reduce((sum, e) => sum + e.quantity, 0);
  
  const todaySmoking = entries
    .filter(e => e.type === HABIT_TYPES.SMOKING)
    .reduce((sum, e) => sum + e.quantity, 0);

  const last30DaysAlcohol = history
    .filter(e => e.type === HABIT_TYPES.ALCOHOL)
    .reduce((sum, e) => sum + e.quantity, 0);
  
  const last30DaysSmoking = history
    .filter(e => e.type === HABIT_TYPES.SMOKING)
    .reduce((sum, e) => sum + e.quantity, 0);

  const totalMoneySpent = history.reduce((sum, e) => {
    const price = e.type === HABIT_TYPES.ALCOHOL ? settings.alcoholPrice : settings.cigarettePrice;
    return sum + (e.quantity * price);
  }, 0);

  // Calculate money saved if quit
  const daysQuit = settings.quitDate 
    ? differenceInDays(new Date(), new Date(settings.quitDate))
    : 0;

  const avgDailyAlcohol = last30DaysAlcohol / 30;
  const avgDailySmoking = last30DaysSmoking / 30;
  
  const moneySaved = daysQuit > 0 
    ? Math.floor((avgDailyAlcohol * settings.alcoholPrice + avgDailySmoking * settings.cigarettePrice) * daysQuit)
    : 0;

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Habit Tracking</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Track and reduce unhealthy habits
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 dark:bg-amber-900 rounded-lg">
              <Beer className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Today's Drinks</p>
              <p className="text-2xl font-bold">{todayAlcohol}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 dark:bg-red-900 rounded-lg">
              <Cigarette className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Today's Cigarettes</p>
              <p className="text-2xl font-bold">{todaySmoking}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">30-Day Spent</p>
              <p className="text-2xl font-bold">₹{totalMoneySpent}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <TrendingDown className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">30-Day Total</p>
              <p className="text-lg font-bold">{last30DaysAlcohol}🍺 {last30DaysSmoking}🚬</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Quit Progress */}
      {settings.quitDate && (
        <Card className="mb-6 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20">
          <div className="text-center">
            <Target className="w-12 h-12 text-green-600 dark:text-green-400 mx-auto mb-3" />
            <h3 className="text-2xl font-bold mb-2">🎉 {daysQuit} Days Clean!</h3>
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-2">
              Money Saved: <span className="font-bold text-green-600 dark:text-green-400">₹{moneySaved}</span>
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Since {format(new Date(settings.quitDate), 'MMM dd, yyyy')}
            </p>
          </div>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Button 
          onClick={() => handleOpenModal(HABIT_TYPES.ALCOHOL)}
          className="bg-amber-600 hover:bg-amber-700"
        >
          <Beer className="w-5 h-5 mr-2" />
          Log Alcohol
        </Button>
        
        <Button 
          onClick={() => handleOpenModal(HABIT_TYPES.SMOKING)}
          className="bg-red-600 hover:bg-red-700"
        >
          <Cigarette className="w-5 h-5 mr-2" />
          Log Smoking
        </Button>

        {!settings.quitDate && (
          <Button 
            onClick={handleSetQuitDate}
            className="bg-green-600 hover:bg-green-700"
          >
            <Target className="w-5 h-5 mr-2" />
            Start Quitting Today!
          </Button>
        )}
      </div>

      {/* Settings */}
      <Card className="mb-6">
        <h3 className="text-lg font-semibold mb-4">💰 Cost Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Price per Drink (₹)
            </label>
            <Input
              type="number"
              value={settings.alcoholPrice}
              onChange={(e) => setSettings(prev => ({ ...prev, alcoholPrice: parseInt(e.target.value) || 0 }))}
              placeholder="500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Price per Cigarette (₹)
            </label>
            <Input
              type="number"
              value={settings.cigarettePrice}
              onChange={(e) => setSettings(prev => ({ ...prev, cigarettePrice: parseInt(e.target.value) || 0 }))}
              placeholder="20"
            />
          </div>
        </div>
        <Button onClick={handleUpdateSettings} className="mt-4">
          Save Settings
        </Button>
      </Card>

      {/* Today's Entries */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">Today's Log</h3>
        {entries.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            {settings.quitDate 
              ? "🎉 Great job! No entries today!" 
              : "No entries yet today"}
          </p>
        ) : (
          <div className="space-y-2">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {entry.type === HABIT_TYPES.ALCOHOL ? (
                    <Beer className="w-5 h-5 text-amber-600" />
                  ) : (
                    <Cigarette className="w-5 h-5 text-red-600" />
                  )}
                  <div>
                    <p className="font-medium">
                      {entry.quantity} {entry.type === HABIT_TYPES.ALCOHOL ? 'drinks' : 'cigarettes'}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      ₹{entry.quantity * entry.price} • {format(new Date(entry.date), 'h:mm a')}
                    </p>
                    {entry.notes && (
                      <p className="text-sm text-gray-500 dark:text-gray-500 italic">
                        {entry.notes}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteEntry(entry.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  Delete
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Add Entry Modal */}
      <Modal 
        isOpen={showAddModal} 
        onClose={() => setShowAddModal(false)}
        title={selectedType === HABIT_TYPES.ALCOHOL ? '🍺 Log Alcohol' : '🚬 Log Smoking'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Quantity ({selectedType === HABIT_TYPES.ALCOHOL ? 'drinks' : 'cigarettes'})
            </label>
            <Input
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
              placeholder="1"
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Notes (optional)
            </label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any context..."
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleAddEntry} className="flex-1">
              Add Entry
            </Button>
            <Button
              onClick={() => setShowAddModal(false)}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default HabitsPage;
